import React, { Dispatch, FC, SetStateAction } from 'react';
import moment, { Moment } from 'moment';
import {
  AkarIconsArrowLeft as ArrowLeft,
  AkarIconsArrowRight as ArrowRight,
} from '..';
import {
  getMonth,
  getYear,
  getPrevMonth,
  getNextMonth,
} from './util/headerFunctions';

export interface DatePickerHeaderProps {
  variant?: 'range' | 'singleRange';
  calendarMoment?: Moment;
  setCalendarMoment?: Dispatch<SetStateAction<Moment | undefined>>;
  fromCalendarMoment?: Moment;
  setFromCalendarMoment?: Dispatch<SetStateAction<Moment | undefined>>;
  toCalendarMoment?: Moment;
  setToCalendarMoment?: Dispatch<SetStateAction<Moment | undefined>>;
  setFocusedDay: Dispatch<SetStateAction<Moment | null>>;
}

export const DatePickerHeader: FC<DatePickerHeaderProps> = ({
  calendarMoment,
  setCalendarMoment,
  variant,
  fromCalendarMoment,
  setFromCalendarMoment,
  toCalendarMoment,
  setToCalendarMoment,
  setFocusedDay,
}: DatePickerHeaderProps) => {
  const isMonthCurrent = (value: Moment) => value.isSame(new Date(), 'month');

  const isAfterNextMonth = (startDate: Moment, endDate: Moment) => {
    const fromDateStartDay = startDate.clone().startOf('month');
    const toDateStartDate = endDate.clone().startOf('month');
    const checkIsAfterNextMonth = toDateStartDate.isAfter(
      getNextMonth(fromDateStartDay),
      'month'
    );

    return checkIsAfterNextMonth;
  };

  // the component will set which calendar cell will be focused when using KB navigation
  const handleFocusedDay = (
    momentObj: Moment | undefined,
    direction: 'previous' | 'next'
  ) => {
    const calendarMonth = momentObj?.month();
    const calendarYear = momentObj?.year();
    if (calendarMonth && calendarYear) {
      if (direction === 'next') {
        const newFocusedDate = moment()
          .year(calendarYear)
          .month(calendarMonth)
          .add(1, 'months')
          .date(1);
        setFocusedDay(newFocusedDate);
      } else if (direction === 'previous') {
        const newFocusedDate = moment()
          .year(calendarYear)
          .month(calendarMonth)
          .subtract(1, 'months')
          .date(1);
        setFocusedDay(newFocusedDate);
      }
    }
  };
  // for the regular datepicker
  const handleSetDateValuePrevMonth = () => {
    if (calendarMoment && setCalendarMoment) {
      setCalendarMoment(getPrevMonth(calendarMoment));
    }
  };

  const handleSetDateValueNextMonth = () => {
    if (calendarMoment && setCalendarMoment) {
      setCalendarMoment(getNextMonth(calendarMoment));
    }
  };

  // for the date range picker

  // left calendar
  const handleSetFromPrevMonth = () => {
    if (fromCalendarMoment && setFromCalendarMoment) {
      const prevMonth = getPrevMonth(fromCalendarMoment);
      setFromCalendarMoment(prevMonth);
    }
  };

  const handleSetFromNextMonth = () => {
    if (fromCalendarMoment && setFromCalendarMoment) {
      setFromCalendarMoment(getNextMonth(fromCalendarMoment));
    }
  };
  // left calendar header
  const handleFromCalendarLeftArrow = () => {
    handleSetFromPrevMonth();
    handleFocusedDay(fromCalendarMoment, 'previous');
  };

  const handleFromCalendarRightArrow = () => {
    handleSetFromNextMonth();
    handleFocusedDay(fromCalendarMoment, 'next');
  };

  // right calendar

  const handleSetToPrevMonth = () => {
    if (toCalendarMoment && setToCalendarMoment) {
      if (!isMonthCurrent(toCalendarMoment))
        setToCalendarMoment(getPrevMonth(toCalendarMoment));
    }
  };

  const handleSetToNextMonth = () => {
    if (toCalendarMoment && setToCalendarMoment) {
      setToCalendarMoment(getNextMonth(toCalendarMoment));
    }
  };
  // right calendar header
  const handleToCalendarLeftArrow = () => {
    handleSetToPrevMonth();
    handleFocusedDay(toCalendarMoment, 'previous');
  };

  const handleToCalendarRightArrow = () => {
    handleSetToNextMonth();
    handleFocusedDay(toCalendarMoment, 'next');
  };

  const renderDefaultHeader = !variant ? (
    <div className="flex w-full justify-between items-center">
      <span
        className="cursor-pointer"
        onClick={() => handleSetDateValuePrevMonth()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleSetDateValuePrevMonth();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Previous Month"
      >
        <ArrowLeft />
      </span>
      <span className="text-black font-open-sans font-semibold text-base">
        {calendarMoment && getMonth(calendarMoment)}{' '}
        {calendarMoment && getYear(calendarMoment)}
      </span>
      <span
        className="cursor-pointer"
        onClick={() => handleSetDateValueNextMonth()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleSetDateValueNextMonth();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Next Month"
      >
        <ArrowRight />
      </span>
    </div>
  ) : null;

  const renderRangeHeader =
    variant &&
    variant === 'range' &&
    fromCalendarMoment &&
    toCalendarMoment &&
    setFromCalendarMoment &&
    setToCalendarMoment ? (
      <div className="flex w-full justify-start items-center">
        <div className="flex w-285px justify-between items-center pt-5 pl-5 pr-5">
          <span
            className="cursor-pointer"
            onClick={handleFromCalendarLeftArrow}
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              (e.key === 'Enter' || e.key === ' ') &&
              handleFromCalendarLeftArrow()
            }
          >
            <ArrowLeft />
          </span>

          <span className="text-black font-open-sans font-semibold text-base">
            {getMonth(fromCalendarMoment)} {getYear(fromCalendarMoment)}
          </span>

          <span
            className="cursor-pointer"
            onClick={handleFromCalendarRightArrow}
            onKeyDown={(e) =>
              (e.key === 'Enter' || e.key === ' ') &&
              handleFromCalendarRightArrow()
            }
            role="button"
            tabIndex={
              isAfterNextMonth(fromCalendarMoment, toCalendarMoment) ? 0 : -1
            }
          >
            {isAfterNextMonth(fromCalendarMoment, toCalendarMoment) ? (
              <ArrowRight />
            ) : null}
          </span>
        </div>

        <div className="flex w-285px justify-between items-center pt-5 pl-5 pr-5">
          <span
            className="cursor-pointer"
            onClick={() =>
              !isMonthCurrent(toCalendarMoment) && handleToCalendarLeftArrow()
            }
            tabIndex={
              isAfterNextMonth(fromCalendarMoment, toCalendarMoment) &&
              getMonth(toCalendarMoment) !== getMonth(fromCalendarMoment)
                ? 0
                : -1
            }
            role="button"
            onKeyDown={(e) =>
              (e.key === 'Enter' || e.key === ' ') &&
              !isMonthCurrent(toCalendarMoment) &&
              handleToCalendarLeftArrow()
            }
          >
            {isAfterNextMonth(fromCalendarMoment, toCalendarMoment) &&
              getMonth(toCalendarMoment) !== getMonth(fromCalendarMoment) && (
                <ArrowLeft />
              )}
          </span>

          {getMonth(fromCalendarMoment) === getMonth(toCalendarMoment) &&
          getYear(toCalendarMoment) === getYear(fromCalendarMoment) ? (
            <span className="text-black font-open-sans font-semibold text-base">
              {toCalendarMoment &&
                getNextMonth(toCalendarMoment).format('MMMM')}{' '}
              {toCalendarMoment &&
                getNextMonth(toCalendarMoment).format('YYYY')}
            </span>
          ) : (
            <span className="text-black font-open-sans font-semibold text-base">
              {getMonth(toCalendarMoment)} {getYear(toCalendarMoment)}
            </span>
          )}

          <span
            className="cursor-pointer"
            onClick={() => {
              handleToCalendarRightArrow();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleToCalendarRightArrow();
              }
            }}
            tabIndex={0}
          >
            <ArrowRight />
          </span>
        </div>
      </div>
    ) : null;

  const renderSingleRangeHeader = variant &&
    variant === 'singleRange' &&
    fromCalendarMoment &&
    toCalendarMoment &&
    setFromCalendarMoment &&
    setToCalendarMoment && (
      <div className="flex w-full justify-start items-center">
        <div className="flex w-285px justify-between items-center pt-5 pl-5 pr-5">
          <span
            className="cursor-pointer"
            onClick={() => handleFromCalendarLeftArrow()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleFromCalendarLeftArrow();
              }
            }}
            role="button"
            tabIndex={0}
          >
            <ArrowLeft />
          </span>

          <span className="text-black font-open-sans font-semibold text-base">
            {getMonth(fromCalendarMoment)} {getYear(fromCalendarMoment)}
          </span>

          <span
            className="cursor-pointer"
            onClick={() => handleFromCalendarRightArrow()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleFromCalendarRightArrow();
              }
            }}
            role="button"
            tabIndex={0}
          >
            <ArrowRight />
          </span>
        </div>
      </div>
    );

  return (
    <>
      {renderDefaultHeader}
      {renderRangeHeader}
      {renderSingleRangeHeader}
    </>
  );
};
